import { useMemo } from 'react'
import { useParams, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useIsPlatformAdmin } from '../hooks/useAuth'
import { usePermissions } from '../contexts/PermissionsContext'
import type { Permission } from '../api/schema'
import { mdiBriefcase, mdiPencil, mdiPinwheel, mdiFolder, mdiShape, mdiFileSign, mdiAccountDetails, mdiFunction, mdiAutoFix, mdiChevronRight, mdiShieldCheck, mdiSwapHorizontal, mdiExport, mdiImport, mdiSigma, mdiTag, mdiAccountGroup, mdiFlagVariant, mdiDomain, mdiCog, mdiTuneVariant, mdiSitemapOutline, mdiVectorLink, mdiShieldAccount, mdiEmailOutline, mdiSync, mdiShieldLockOutline, mdiArchiveOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { useBillingSummary } from '../hooks/use-billing'

interface SettingsLink {
  to: string
  params: Record<string, string>
  search?: Record<string, any>
  icon: string
  title: string
  description: string
  testId?: string
  /**
   * The `read` permission this destination needs. A link the caller cannot
   * follow is not rendered — the route guard in routes/$org.tsx would bounce
   * them straight back with a denial banner. Omit for ungated destinations.
   */
  permission?: Permission
}

export function OrganizationSettingsPage() {
  const { org } = useParams({ strict: true, from: '/$org' })
  const { t } = useTranslation('settings')
  const { t: tRoles } = useTranslation('roles')
  const { t: tWebhooks } = useTranslation('webhooks')
  const isPlatformAdmin = useIsPlatformAdmin()
  const { permissions } = usePermissions()
  // Hiding a settings entry is a courtesy, not a control: the backend gates
  // every one of these routes (spec 2026-09-04-02).
  const visible = (links: SettingsLink[], groupPermission?: Permission) =>
    links.filter((l) => {
      const needed = l.permission ?? groupPermission
      return !needed || permissions.includes(needed)
    })

  // Set breadcrumbs
  const breadcrumbs = useMemo(() => [
    { title: t('common:nav.home'), path: `/${org}` },
    { title: t('page.title') }
  ], [org, t])

  useBreadcrumbs(breadcrumbs)

  // The organization's own identity — name, slug, logo, language. First,
  // because it is the thing every other group configures the inside of
  // (spec 2026-09-09-10 §4).
  const organizationLinks: SettingsLink[] = [
    {
      to: '/$org/settings/organization',
      params: { org },
      icon: mdiDomain,
      title: t('links.organization_title'),
      description: t('links.organization_description'),
      testId: 'organization-settings-link',
    },
  ]

  const ontologyLinks: SettingsLink[] = [
    {
      to: '/$org/asset-types',
      params: { org },
      icon: mdiShape,
      title: t('links.asset_types_title'),
      description: t('links.asset_types_description'),
    },
    {
      to: '/$org/properties',
      params: { org },
      icon: mdiPencil,
      title: t('links.properties_title'),
      description: t('links.properties_description'),
    },
    {
      to: '/$org/derivations',
      params: { org },
      icon: mdiFunction,
      title: t('links.derivations_title'),
      description: t('links.derivations_description'),
      testId: 'derivations-btn',
    },
    {
      to: '/$org/normalizations',
      params: { org },
      icon: mdiAutoFix,
      title: t('links.normalizations_title'),
      description: t('links.normalizations_description'),
      testId: 'normalizations-btn',
    },
    {
      to: '/$org/aggregations',
      params: { org },
      icon: mdiSigma,
      title: t('links.aggregations_title'),
      description: t('links.aggregations_description'),
      testId: 'aggregations-btn',
    },
    {
      to: '/$org/property-mapping-rules',
      params: { org },
      icon: mdiSwapHorizontal,
      title: t('links.property_mappings_title'),
      description: t('links.property_mappings_description'),
      testId: 'property-mapping-rules-btn',
    },
    {
      to: '/$org/settings/tags',
      params: { org },
      icon: mdiTag,
      title: t('links.tags_title'),
      description: t('links.tags_description'),
      testId: 'tags-btn',
    },
    {
      to: '/$org/relation-types',
      params: { org },
      icon: mdiVectorLink,
      title: t('links.relation_types_title'),
      description: t('links.relation_types_description'),
      testId: 'relation-types-btn',
    },
  ]

  const documentLinks: SettingsLink[] = [
    {
      to: '/$org/document-types',
      params: { org },
      icon: mdiBriefcase,
      title: t('links.document_types_title'),
      description: t('links.document_types_description'),
    },
    {
      to: '/$org/folder-layouts',
      params: { org },
      search: { search: '' },
      icon: mdiFolder,
      title: t('links.folder_layouts_title'),
      description: t('links.folder_layouts_description'),
    },
  ]

  const contractLinks: SettingsLink[] = [
    {
      to: '/$org/contract-types',
      params: { org },
      icon: mdiFileSign,
      title: t('links.contract_types_title'),
      description: t('links.contract_types_description'),
    },
    {
      to: '/$org/contract-stakeholder-types',
      params: { org },
      icon: mdiAccountDetails,
      title: t('links.stakeholder_types_title'),
      description: t('links.stakeholder_types_description'),
    },
  ]

  const complianceLinks: SettingsLink[] = [
    {
      to: '/$org/business-rules',
      params: { org },
      icon: mdiTuneVariant,
      title: t('links.business_rules_title'),
      description: t('links.business_rules_description'),
      testId: 'business-rules-btn',
    },
    {
      to: '/$org/remediation-scenarios',
      params: { org },
      icon: mdiSitemapOutline,
      title: t('links.remediation_scenarios_title'),
      description: t('links.remediation_scenarios_description'),
      testId: 'remediation-scenarios-btn',
    },
    {
      to: '/$org/compliance-controls',
      params: { org },
      icon: mdiShieldCheck,
      title: t('links.compliance_controls_title'),
      description: t('links.compliance_controls_description'),
      testId: 'compliance-controls-btn',
    },
    {
      to: '/$org/compliance-rules',
      params: { org },
      icon: mdiShieldCheck,
      title: t('links.compliance_rules_title'),
      description: t('links.compliance_rules_description'),
      testId: 'compliance-rules-btn',
    },
    {
      to: '/$org/data-protection',
      params: { org },
      icon: mdiShieldLockOutline,
      title: t('links.data_protection_title'),
      description: t('links.data_protection_description'),
      testId: 'data-protection-btn',
      permission: 'data_protection.read',
    },
  ]

  const importExportLinks: SettingsLink[] = [
    {
      to: '/$org/types/export',
      params: { org },
      icon: mdiExport,
      title: t('links.types_export_title'),
      description: t('links.types_export_description'),
      testId: 'types-export-btn',
    },
    {
      to: '/$org/types/import',
      params: { org },
      icon: mdiImport,
      title: t('links.types_import_title'),
      description: t('links.types_import_description'),
      testId: 'types-import-btn',
    },
    {
      to: '/$org/types/sync',
      params: { org },
      icon: mdiSync,
      title: t('links.types_sync_title'),
      description: t('links.types_sync_description'),
      testId: 'types-sync-btn',
    },
    // Export/import/quality is something one does to the organization's own
    // records, not the type registry — a different subject sharing this
    // group, hence its own `data.read` permission rather than the group's
    // default `configuration.read` (spec 2026-09-18-08).
    {
      to: '/$org/data',
      params: { org },
      icon: SECTION_ICONS.data,
      title: t('links.data_title'),
      description: t('links.data_description'),
      testId: 'data-link',
      permission: 'data.read',
    },
  ]

  // Where mail can arrive is configuration, so it sits in the settings index
  // beside the rest of it (spec 2026-09-09-13 § 1).
  const conversationLinks: SettingsLink[] = [
    {
      to: '/$org/conversations/intake-addresses',
      params: { org },
      icon: mdiEmailOutline,
      title: t('links.intake_addresses_title'),
      description: t('links.intake_addresses_description'),
      testId: 'intake-addresses-link',
      permission: 'conversations.read',
    },
  ]

  const systemLinks: SettingsLink[] = [
    {
      to: '/$org/settings/feature-flags',
      params: { org },
      icon: mdiFlagVariant,
      title: t('links.feature_flags_title'),
      description: t('links.feature_flags_description'),
      testId: 'feature-flags-link',
      permission: 'organization.read',
    },
    {
      to: '/$org/jobs',
      params: { org },
      search: { type: undefined, status: undefined, limit: 30, offset: 0 },
      icon: mdiPinwheel,
      title: t('links.jobs_title'),
      description: t('links.jobs_description'),
      permission: 'data.read',
    },
    {
      // Which declared AI provider and model run extraction (spec
      // 2026-10-10-01): readable by members, writable by platform admins.
      to: '/$org/settings/ai-models',
      params: { org },
      icon: mdiAutoFix,
      title: t('links.ai_models_title'),
      description: t('links.ai_models_description'),
      testId: 'ai-models-link',
      permission: 'organization.read',
    },
    {
      // Archived items (spec 2026-10-08-02): owner and admin only.
      to: '/$org/settings/archive',
      params: { org },
      icon: mdiArchiveOutline,
      title: t('links.archive_title'),
      description: t('links.archive_description'),
      testId: 'archive-link',
      permission: 'archive.read',
    },
  ]

  // The customer's billing (spec 2026-10-05-15): a card only for the members
  // who hold `billing.read`, and only when the organization is billed.
  const canReadBilling = permissions.includes('billing.read')
  const { data: billingSummary } = useBillingSummary(org, canReadBilling)
  const billingLinks: SettingsLink[] = billingSummary?.billable
    ? [
        {
          to: '/$org/settings/billing',
          params: { org },
          icon: SECTION_ICONS.adminBilling,
          title: t('links.billing_title'),
          description: t('links.billing_description'),
          testId: 'billing-link',
          permission: 'billing.read',
        },
      ]
    : []

  const accessLinks: SettingsLink[] = [
    {
      to: '/$org/settings/members',
      params: { org },
      icon: mdiAccountGroup,
      title: t('links.members_title'),
      description: t('links.members_description'),
      testId: 'members-link',
      permission: 'members.read',
    },
    {
      to: '/$org/settings/roles',
      params: { org },
      search: { role: undefined, from: undefined },
      icon: mdiShieldAccount,
      title: tRoles('settings_link.title'),
      description: tRoles('settings_link.description'),
      testId: 'roles-link',
      permission: 'members.read',
    },
    {
      to: '/$org/settings/security',
      params: { org },
      icon: SECTION_ICONS.security,
      title: t('links.security_title'),
      description: t('links.security_description'),
      testId: 'security-link',
      permission: 'organization.read',
    },
    {
      to: '/$org/settings/webhooks',
      params: { org },
      icon: SECTION_ICONS.webhooks,
      title: tWebhooks('settings_link.title'),
      description: tWebhooks('settings_link.description'),
      testId: 'webhooks-link',
      permission: 'webhooks.read',
    },
  ]

  // Cross-org, server-wide surfaces — deliberately org-less routes (see the
  // placement rationale comments on their own route files), so no `org`
  // path param to pass.
  const platformAdminLinks: SettingsLink[] = [
    {
      to: '/organizations',
      params: {},
      icon: mdiDomain,
      title: t('links.admin_organizations_title'),
      description: t('links.admin_organizations_description'),
      testId: 'admin-organizations-link',
    },
    {
      to: '/admin/settings',
      params: {},
      icon: mdiCog,
      title: t('links.admin_settings_title'),
      description: t('links.admin_settings_description'),
      testId: 'admin-settings-link',
    },
    {
      to: '/admin/usage',
      params: {},
      icon: SECTION_ICONS.adminUsage,
      title: t('links.admin_usage_title'),
      description: t('links.admin_usage_description'),
      testId: 'admin-usage-link',
    },
    {
      to: '/admin/billing',
      params: {},
      icon: SECTION_ICONS.adminBilling,
      title: t('links.admin_billing_title'),
      description: t('links.admin_billing_description'),
      testId: 'admin-billing-link',
    },
    {
      to: '/admin/rent-indices',
      params: {},
      icon: SECTION_ICONS.adminRentIndices,
      title: t('links.admin_rent_indices_title'),
      description: t('links.admin_rent_indices_description'),
      testId: 'admin-rent-indices-link',
    },
    {
      to: '/admin/proposed-rules',
      params: {},
      icon: SECTION_ICONS.businessRules,
      title: t('links.admin_proposed_rules_title'),
      description: t('links.admin_proposed_rules_description'),
      testId: 'admin-proposed-rules-link',
    },
    {
      to: '/admin/agent-pool',
      params: {},
      icon: SECTION_ICONS.adminAgentPool,
      title: t('links.admin_agent_pool_title'),
      description: t('links.admin_agent_pool_description'),
      testId: 'admin-agent-pool-link',
    },
  ]

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <PageHeader
        className="mb-8"
        icon={SECTION_ICONS.settings}
        title={t('page.title')}
        description={t('page.subtitle')}
      />

      {/* Settings Groups */}
      <div className="space-y-6">
        <SettingsGroup title={t('groups.organization')} description={t('groups.organization_description')} links={visible(organizationLinks)} org={org} />
        <SettingsGroup title={t('groups.access')} description={t('groups.access_description')} links={visible(accessLinks)} org={org} />
        <SettingsGroup title={t('groups.billing')} description={t('groups.billing_description')} links={visible(billingLinks)} org={org} />
        <SettingsGroup title={t('groups.ontology')} description={t('groups.ontology_description')} links={visible(ontologyLinks, 'configuration.read')} org={org} />
        <SettingsGroup title={t('groups.documents')} description={t('groups.documents_description')} links={visible(documentLinks, 'configuration.read')} org={org} />
        <SettingsGroup title={t('groups.contracts')} description={t('groups.contracts_description')} links={visible(contractLinks, 'configuration.read')} org={org} />
        <SettingsGroup title={t('groups.compliance')} description={t('groups.compliance_description')} links={visible(complianceLinks, 'compliance.read')} org={org} />
        <SettingsGroup title={t('groups.import_export')} description={t('groups.import_export_description')} links={visible(importExportLinks, 'configuration.read')} org={org} />
        <SettingsGroup title={t('groups.conversations')} description={t('groups.conversations_description')} links={visible(conversationLinks)} org={org} />
        <SettingsGroup title={t('groups.system')} description={t('groups.system_description')} links={visible(systemLinks)} org={org} />
        {isPlatformAdmin && (
          <SettingsGroup
            title={t('groups.platform_admin')}
            description={t('groups.platform_admin_description')}
            links={platformAdminLinks}
            org={org}
          />
        )}
      </div>
    </div>
  )
}

function SettingsGroup({ title, description, links }: { title: string; description: string; links: SettingsLink[]; org: string }) {
  // An entirely permission-hidden group leaves no heading behind.
  if (links.length === 0) return null
  return (
    <div>
      <div className="mb-2">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{title}</h2>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="bg-card rounded-lg border border-border divide-y divide-border">
        {links.map((link) => (
          <Link
            key={link.title}
            to={link.to}
            params={link.params}
            search={link.search as any}
            data-testid={link.testId}
            className="flex items-center gap-4 px-4 py-3.5 hover:bg-accent/50 transition-colors first:rounded-t-lg last:rounded-b-lg"
          >
            <div className="flex items-center justify-center w-9 h-9 rounded-md bg-accent/60 shrink-0">
              <AppIcon path={link.icon} size="md" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground">{link.title}</div>
              <div className="text-xs text-muted-foreground">{link.description}</div>
            </div>
            <AppIcon tone="muted" path={mdiChevronRight} size="md" className="shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  )
}
